import { Component, OnInit, inject } from '@angular/core';
import { Base64 } from 'js-base64';
import { ActivatedRoute } from '@angular/router';
import { CaptureQueueCursorType } from 'app/core/enums/capture-queue-cursor-type.enum';
import { CaptureQueueResult } from 'app/core/models/capture-lists/capture-queue-result.model';
import { Cursor } from 'app/core/models/paged-results/cursor-based-paging.model';
import { Provider } from 'app/core/models/provider.model';
import { ProviderCapturesSearchFilter, ProviderCapturesSearchSettings } from 'app/core/models/user-settings/provider-captures-search-settings.model';
import { ProviderService } from 'app/core/services/provider.service';
import { Subject } from 'rxjs';
import { ProviderCapturesService } from 'app/core/services/provider-captures.service';
import { NavigationService, PageChangeEvent } from 'app/core/services/navigation.service';
import { PagedCaptureResults } from 'app/core/services/patient-captures.service';
import { switchMap, tap } from 'rxjs/operators';

import { ProviderHeaderComponent } from '../../components/provider-header/provider-header.component';
import { ProviderCapturesSearchSettingsComponent } from '../../components/provider-captures-search-settings/provider-captures-search-settings.component';
import { CaptureQueueResultsComponent } from '../../components/capture-queue-results/capture-queue-results.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-capture-admin-provider-captures',
  templateUrl: './capture-admin-provider-captures.component.html',
  styleUrls: ['./capture-admin-provider-captures.component.scss'],
  imports: [ProviderHeaderComponent,
    ProviderCapturesSearchSettingsComponent,
    CaptureQueueResultsComponent,
    MatProgressSpinner]
})
export class CaptureAdminProviderCapturesComponent implements OnInit {
  private providerService = inject(ProviderService);
  private route = inject(ActivatedRoute);
  private providerCapturesService = inject(ProviderCapturesService);
  private navigationService = inject(NavigationService);

  getCaptures$: Subject<void> = new Subject();

  provider: Provider;
  loading: boolean;

  settings: ProviderCapturesSearchSettings;
  captureQueueResults: CaptureQueueResult[] = [];
  cursor: Cursor<CaptureQueueCursorType>;
  loadingCaptures = true;
  hasMoreCaptures = false;
  limit = 10;

  constructor() {
    this.navigationService.pageChanged.subscribe((pageChangeEvent: PageChangeEvent) => {
      if (pageChangeEvent.navigatedBack && !this.loading) {
        this.initCursorFromParams();
        this.getCaptures$.next();
      }
    });
  }

  ngOnInit() {
    this.init();
  }

  onSettingsChange(settings: ProviderCapturesSearchSettings) {
    this.settings = settings;
    this.resetCursor();
    this.getCaptures();
  }

  onNextClick($event) {
    $event.preventDefault();
    this.updateQueryParams();
    this.getCaptures();
    window.scrollTo(0, 0);
  }

  private init() {
    const npi = this.route.snapshot.paramMap.get('npi');
    this.loading = true;
    this.providerService.getByNpi(npi).subscribe(provider => {
      this.provider = provider;
      this.loading = false;

      this.resetCursor();
      this.initSettings();
      this.configureGetCaptures();
      this.getCaptures();
    });
  }

  private initSettings() {
    this.settings = new ProviderCapturesSearchSettings(
      new ProviderCapturesSearchFilter(),
      CaptureQueueCursorType.highestEstimatedValue
    );
  }

  private getCaptures() {
    this.getCaptures$.next();
  }

  private configureGetCaptures() {
    // https://www.credera.com/blog/technology-solutions/using-rxjs-switchmap-angular-7-reactive-forms-cancel-pending-requests/
    this.getCaptures$
      .pipe(
        tap(() => {
          this.loadingCaptures = true;
          this.captureQueueResults = [];
        }),
        switchMap(() => this.fetchCaptures())
      )
      .subscribe((results: PagedCaptureResults) => {
        this.captureQueueResults = results.captureQueueResults;
        this.cursor = results.meta.cursor;
        this.loadingCaptures = false;
        this.hasMoreCaptures = !!results.meta.cursor;
      });
  }

  private fetchCaptures() {
    return this.providerCapturesService.getCaptures(
      this.provider.id, this.cursor, this.settings, this.limit
    );
  }

  private resetCursor() {
    this.cursor = null;
    this.updateQueryParams();
  }

  private updateQueryParams() {
    if (this.cursor) {
      const encodedCursor = encodeURIComponent(Base64.encode(JSON.stringify(this.cursor)));
      this.navigationService.updateQueryParams({ cursor: encodedCursor });
    } else {
      this.navigationService.updateQueryParams({}, true);
    }
  }

  private initCursorFromParams() {
    const queryParams = this.navigationService.queryParams;

    if ('cursor' in queryParams) {
      this.cursor = this.decodeCursor(queryParams);
    } else {
      this.cursor = null;
    }
  }

  private decodeCursor(queryParams) {
    try {
      const cursorParam = decodeURIComponent(queryParams['cursor']);
      return JSON.parse(Base64.decode(cursorParam));
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
